import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Trash2,
  Clipboard,
  Eye,
  Calendar,
  User,
  Clock,
  ChevronDown,
  Smartphone,
  MapPin,
  Castle,
  Ship,
  Globe,
  Sparkles,
  CheckCircle,
  AlertCircle,
  Loader2,
  Edit3,
  Save,
  X,
  Star,
  FileText,
  StickyNote,
} from "lucide-react";
import { formatDate } from "../../../utils/browserCompatibility";
import { decodeHtmlEntities } from "../../../utils/textUtils";
import toast from "../../../utils/toast";
import { DollarSign } from "lucide-react";
import CommissionEditModal from "./CommissionEditModal";
import QuestionnaireSummaryModal from "./QuestionnaireSummaryModal";
import TripNotesEditorModal from "./TripNotesEditorModal";
import ShareClientModal from "./ShareClientModal";

const EnhancedTripRow = ({
  trip,
  client,
  onDelete,
  onCopyLink,
  onUnifiedNavigation,
  onStatusUpdate,
  onClientStatusUpdate,
  onCommissionUpdate,
  onDocumentUpload,
  hasCRMAccess = false
}) => {
  const navigate = useNavigate();
  const [isEditingStatus, setIsEditingStatus] = useState(false);
  const [newStatus, setNewStatus] = useState(() => (trip.status ?? trip.Status ?? 'pending').toString().toLowerCase());
  const [updating, setUpdating] = useState(false);
  const [showCommissionModal, setShowCommissionModal] = useState(false);
  const [showAgentNotesModal, setShowAgentNotesModal] = useState(false);
  const [showClientNotesModal, setShowClientNotesModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showMoreActions, setShowMoreActions] = useState(false);
  const [commissionData, setCommissionData] = useState(trip.commission || null);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowMoreActions(false);
      }
    };

    if (showMoreActions) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMoreActions]);

  // Commission data will be managed by the parent component or commission tracking system
  // Individual trip commission fetching removed to avoid API overload

  // Use the browser-compatible formatDate from utils
  const formatTripDate = (dateString) => {
    return formatDate(dateString, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };

  // Normalize status from API (may be status or Status, any casing)
  const normalizedStatus = (trip.status ?? trip.Status ?? 'pending').toString().toLowerCase();

  // Sync local edit state when trip prop updates (e.g. after refetch)
  useEffect(() => {
    setNewStatus(normalizedStatus);
  }, [normalizedStatus]);

  // Enhanced status management (includes planning/active so questionnaire submit shows correctly)
  const getStatusInfo = (status) => {
    const key = (status ?? 'pending').toString().toLowerCase();
    const statusMap = {
      'pending': {
        label: 'Pending',
        color: 'bg-yellow-100 text-yellow-800 border-yellow-200',
        icon: Clock,
        description: 'Awaiting questionnaire'
      },
      'planning': {
        label: 'Planning',
        color: 'bg-amber-100 text-amber-800 border-amber-200',
        icon: FileText,
        description: 'Questionnaire submitted, generating itinerary'
      },
      'confirmed': {
        label: 'Confirmed',
        color: 'bg-blue-100 text-blue-800 border-blue-200',
        icon: CheckCircle,
        description: `Booked with ${getDestinationInfo().name}`
      },
      'active': {
        label: 'Active',
        color: 'bg-indigo-100 text-indigo-800 border-indigo-200',
        icon: Clock,
        description: 'Client is on the trip'
      },
      'completed': {
        label: 'Completed',
        color: 'bg-green-100 text-green-800 border-green-200',
        icon: CheckCircle,
        description: 'Trip finished successfully'
      },
      'cancelled': {
        label: 'Cancelled',
        color: 'bg-red-100 text-red-800 border-red-200',
        icon: AlertCircle,
        description: 'Trip was cancelled'
      }
    };
    return statusMap[key] || statusMap['pending'];
  };

  const handleStatusUpdate = async () => {
    if (!hasCRMAccess) {
      toast.error("CRM access required to update trip status");
      return;
    }

    const current = (trip.status ?? trip.Status ?? 'pending').toString().toLowerCase();
    if (newStatus === current) {
      setIsEditingStatus(false);
      return;
    }

    setUpdating(true);
    try {
      // Update trip status
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/trips/${trip.tripid}`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ status: newStatus }),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        const message = data?.message || data?.details?.[0]?.message || 'Failed to update trip status';
        throw new Error(message);
      }

      // If trip is completed, update client status to customer
      if (newStatus === 'completed' && onClientStatusUpdate && client?.clientid) {
        await onClientStatusUpdate(client.clientid, 'customer');
        toast.success("Trip completed and client status updated to customer!");
      } else {
        toast.success(`Trip status updated to ${newStatus}`);
      }

      // Call parent callback to refresh data
      if (onStatusUpdate) {
        onStatusUpdate(trip.tripid, newStatus);
      }

      setIsEditingStatus(false);
    } catch (error) {
      console.error('Error updating trip status:', error);
      toast.error('Failed to update trip status');
    } finally {
      setUpdating(false);
    }
  };

  const cancelStatusEdit = () => {
    setNewStatus((trip.status ?? trip.Status ?? 'pending').toString().toLowerCase());
    setIsEditingStatus(false);
  };

  // Handle commission updates
  const handleCommissionUpdate = (newCommissionData) => {
    setCommissionData(newCommissionData);
    if (onCommissionUpdate) {
      onCommissionUpdate(newCommissionData);
    }
  };

  // Itinerary is viewable once questionnaire is submitted (generated in seconds)
  const isTripViewable = () => {
    return normalizedStatus === "planning" || normalizedStatus === "completed";
  };

  const getClientName = () => {
    if (client?.fullname) return decodeHtmlEntities(client.fullname);
    if (trip.fullname) return decodeHtmlEntities(trip.fullname);
    return "Unknown Client";
  };

  const getTripTitle = () => {
    if (trip.tripname) return decodeHtmlEntities(trip.tripname);
    if (trip.name) return decodeHtmlEntities(trip.name);
    return "Untitled Trip";
  };

  const getDestinationInfo = () => {
    const destination = trip.destination || 'disney_world';
    const destinationMap = {
      'disney_world': {
        name: 'Disney World',
        icon: Sparkles,
        color: 'gold',
        bgColor: 'bg-yellow-100',
        textColor: 'text-yellow-600'
      },
      'disneyland': {
        name: 'Disneyland Resort',
        icon: Castle,
        color: 'purple',
        bgColor: 'bg-purple-100',
        textColor: 'text-purple-600'
      },
      'universal_orlando': {
        name: 'Universal Orlando',
        icon: Globe,
        color: 'green',
        bgColor: 'bg-green-100',
        textColor: 'text-green-600'
      },
      'disney_cruise': {
        name: 'Disney Cruise Line',
        icon: Ship,
        color: 'teal',
        bgColor: 'bg-teal-100',
        textColor: 'text-teal-600'
      }
    };

    return destinationMap[destination] || destinationMap['disney_world'];
  };

  // Check if questionnaire has been answered (used to show Questionnaire Summary when meaningful)
  const hasQuestionnaireData = () => {
    // Check if trip has explicit questionnaire data (API may return questionnaireData or questionnairedata)
    const hasData = trip.questionnaireData ?? trip.questionnairedata;
    if (hasData && (typeof hasData === 'object' ? Object.keys(hasData).length > 0 : String(hasData).trim())) {
      return true;
    }
    // Trip status indicates questionnaire completion (planning = submitted, etc.)
    return normalizedStatus && normalizedStatus !== 'pending';
  };

  const destinationInfo = getDestinationInfo();
  const DestinationIcon = destinationInfo.icon;
  const statusInfo = getStatusInfo(normalizedStatus);
  const StatusIcon = statusInfo.icon;

  return (
    <div className="bg-white rounded-lg border border-gray-200 transition-colors hover:bg-gray-50">
      {/* Main Trip Row */}
      <div className="flex justify-between items-center p-4">
        {/* Trip Info - Left Side */}
        <div className="flex flex-1 items-center space-x-4 min-w-0">
          <div className="flex-shrink-0">
            <div className="flex justify-center items-center w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg">
              <Calendar className="w-6 h-6 text-white" />
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-semibold text-gray-900 truncate">
              {getTripTitle()}
            </h3>
            <div className="flex items-center mt-1 space-x-4 text-sm text-gray-500">
              <div className="flex items-center space-x-1">
                <User className="w-4 h-4" />
                <span className="truncate">{getClientName()}</span>
              </div>
              <div className="flex items-center space-x-1">
                <Clock className="w-4 h-4" />
                <span>Created {formatTripDate(trip.createdat)}</span>
              </div>
              <div className="flex items-center space-x-1">
                <DestinationIcon className={`w-4 h-4 ${destinationInfo.textColor}`} />
                <span className={destinationInfo.textColor}>{destinationInfo.name}</span>
              </div>
            </div>

            {/* Commission Display - Only show if has CRM access and commission data */}
            {hasCRMAccess && commissionData && (
              <div className="flex items-center mt-2 space-x-4 text-sm">
                <div className="flex items-center space-x-1 text-gray-600">
                  <DollarSign className="w-4 h-4 text-green-600" />
                  <span className="font-medium">
                    Commission: ${commissionData.amount || '0.00'}
                  </span>
                  {commissionData.rate && (
                    <span className="text-gray-500">
                      ({((commissionData.rate || 0) * 100).toFixed(1)}%)
                    </span>
                  )}
                </div>
                {commissionData.tripValue && (
                  <div className="text-gray-500">
                    Trip Value: ${commissionData.tripValue}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Status and Primary Actions - Right Side */}
        <div className="flex items-center space-x-3">
          {/* Status */}
          <div className="flex items-center space-x-2">
            {isEditingStatus ? (
              <div className="flex items-center space-x-2">
                <div className="relative">
                  <select
                    value={newStatus}
                    onChange={(e) => setNewStatus(e.target.value)}
                    className="appearance-none pl-3 pr-8 py-2 text-sm font-medium bg-white border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 hover:border-gray-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed min-w-[100px]"
                    disabled={updating}
                  >
                    <option value="pending">Pending</option>
                    <option value="planning">Planning</option>
                    <option value="confirmed">Confirmed</option>
                    <option value="active">Active</option>
                    <option value="completed">Completed</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                  <div className="flex absolute inset-y-0 right-0 items-center pr-2 pointer-events-none">
                    <ChevronDown className="w-4 h-4 text-gray-400" />
                  </div>
                </div>

                <button
                  onClick={handleStatusUpdate}
                  disabled={updating}
                  className="p-2 text-green-600 rounded-md transition-colors hover:text-green-700 hover:bg-green-50 disabled:opacity-50"
                  title="Save status"
                >
                  {updating ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4" />
                  )}
                </button>

                <button
                  onClick={cancelStatusEdit}
                  disabled={updating}
                  className="p-2 text-gray-500 rounded-md transition-colors hover:text-gray-700 hover:bg-gray-50"
                  title="Cancel"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div className="flex items-center space-x-2">
                <span
                  className={`px-3 py-1 text-sm font-medium rounded-full border flex items-center space-x-2 ${statusInfo.color}`}
                >
                  <StatusIcon className="w-4 h-4" />
                  <span>{statusInfo.label}</span>
                </span>

                {hasCRMAccess && (
                  <button
                    onClick={() => setIsEditingStatus(true)}
                    className="p-2 text-gray-400 rounded-md transition-colors hover:text-gray-600 hover:bg-gray-100"
                    title="Edit status"
                  >
                    <Edit3 className="w-4 h-4" />
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Primary Action Buttons */}
          <div className="flex items-center space-x-2">
            {(normalizedStatus === 'planning' || normalizedStatus === 'completed') ? (
              <>
                {/* View Itinerary - available as soon as questionnaire is submitted (itinerary generates in seconds) */}
                <button
                  onClick={() => {
                    if (!isTripViewable()) {
                      toast.info("This trip must be completed before viewing the itinerary.");
                      return;
                    }
                    if (onUnifiedNavigation) {
                      onUnifiedNavigation(trip.tripid);
                    } else {
                      console.error('[EnhancedTripRow] onUnifiedNavigation hook not provided - cannot navigate to itinerary');
                      toast.error('Navigation error: Please refresh the page and try again.');
                    }
                  }}
                  className="flex items-center space-x-1 px-2 sm:px-3 py-1 sm:py-1.5 text-xs sm:text-sm font-medium text-blue-600 border border-blue-500 rounded-md hover:bg-blue-50 transition-colors whitespace-nowrap"
                >
                  <Eye className="flex-shrink-0 w-3 h-3 sm:w-4 sm:h-4" />
                  <span className="hidden sm:inline">View Itinerary</span>
                </button>

                {/* Share with Client - available until trip is completed, then removed */}
                {normalizedStatus !== 'completed' && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowShareModal(true);
                    }}
                    className="flex items-center space-x-1 px-2 sm:px-3 py-1 sm:py-1.5 text-xs sm:text-sm font-medium text-purple-600 border border-purple-500 rounded-md hover:bg-purple-50 transition-colors whitespace-nowrap"
                  >
                    <Smartphone className="flex-shrink-0 w-3 h-3 sm:w-4 sm:h-4" />
                    <span className="hidden sm:inline">Share with Client</span>
                  </button>
                )}
              </>
            ) : (
              <div className="px-3 py-2 text-sm text-gray-500">
                {statusInfo.description}
              </div>
            )}

            {/* More Actions Dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowMoreActions(!showMoreActions);
                }}
                className="p-2 text-gray-400 rounded-md transition-colors hover:text-gray-600 hover:bg-gray-100"
                title="More actions"
              >
                <ChevronDown className={`w-4 h-4 transition-transform ${showMoreActions ? 'rotate-180' : ''}`} />
              </button>

              {/* Dropdown Menu */}
              {showMoreActions && (
                <div className="absolute right-0 top-full z-10 mt-1 w-56 bg-white rounded-md border border-gray-200 shadow-lg">
                  <div className="py-1">
                    {/* Commission Edit - Only for CRM Access */}
                    {hasCRMAccess && (
                      <button
                        onClick={() => {
                          setShowCommissionModal(true);
                          setShowMoreActions(false);
                        }}
                        className="flex items-center px-4 py-2 w-full text-sm text-green-600 transition-colors hover:bg-green-50"
                      >
                        <DollarSign className="mr-3 w-4 h-4" />
                        Edit Commission
                      </button>
                    )}

                    {/* Copy Questionnaire Link - available for all trips so agent can resend anytime */}
                    <button
                      onClick={() => {
                        const formLink = `${window.location.origin}/questionnaire/${trip.tripid}`;
                        const link = trip.questionnaireLink || trip.questionnairelink || formLink;
                        if (onCopyLink) {
                          onCopyLink(link);
                        } else {
                          navigator.clipboard.writeText(link).then(() => {
                            toast.success("Questionnaire link copied to clipboard!");
                          });
                        }
                        setShowMoreActions(false);
                      }}
                      className="flex items-center px-4 py-2 w-full text-sm text-blue-600 whitespace-nowrap transition-colors hover:bg-blue-50"
                    >
                      <Clipboard className="mr-3 w-4 h-4 shrink-0" />
                      Copy Questionnaire Link
                    </button>

                    {/* Questionnaire Summary - only show when trip has questionnaire data */}
                    {hasQuestionnaireData() && (
                      <button
                        onClick={() => {
                          setShowAgentNotesModal(true);
                          setShowMoreActions(false);
                        }}
                        className="flex items-center px-4 py-2 w-full text-sm text-gray-600 transition-colors hover:bg-gray-50"
                      >
                        <Star className="mr-3 w-4 h-4" />
                        Questionnaire Summary
                      </button>
                    )}

                    {/* Trip Notes - Trip-specific notes (separate from Client Notes) */}
                    <button
                      onClick={() => {
                        setShowClientNotesModal(true);
                        setShowMoreActions(false);
                      }}
                      className="flex items-center px-4 py-2 w-full text-sm text-gray-600 transition-colors hover:bg-gray-50"
                    >
                      <StickyNote className="mr-3 w-4 h-4" />
                      Trip Notes
                    </button>

                    {/* View Client */}
                    {client && (
                      <button
                        onClick={() => {
                          const clientId = client.clientid || trip.clientid;
                          if (clientId) {
                            navigate(`/agent/crm/clients/${clientId}`);
                          } else {
                            // Fallback to general clients page if no clientId available
                            navigate('/agent/crm/clients');
                          }
                          setShowMoreActions(false);
                        }}
                        className="flex items-center px-4 py-2 w-full text-sm text-gray-600 transition-colors hover:bg-gray-50"
                      >
                        <User className="mr-3 w-4 h-4" />
                        View Client
                      </button>
                    )}

                    {/* Copy Link - Only for completed trips */}
                    {normalizedStatus === 'completed' && (
                      <button
                        onClick={() => {
                          // Fix: Use correct route path with /agent prefix
                          const link = `${window.location.origin}/agent/itinerary/view/${trip.tripid}`;
                          if (onCopyLink) {
                            onCopyLink(link);
                          } else {
                            navigator.clipboard.writeText(link).then(() => {
                              toast.success("Link copied to clipboard!");
                            });
                          }
                          setShowMoreActions(false);
                        }}
                        className="flex items-center px-4 py-2 w-full text-sm text-gray-600 transition-colors hover:bg-gray-50"
                      >
                        <Clipboard className="mr-3 w-4 h-4" />
                        Copy Link
                      </button>
                    )}

                    {/* Upload Docs - Only for completed trips */}
                    {normalizedStatus === 'completed' && (
                      <button
                        onClick={() => {
                          if (onDocumentUpload) {
                            onDocumentUpload(trip);
                          }
                          setShowMoreActions(false);
                        }}
                        className="flex items-center px-4 py-2 w-full text-sm text-green-600 transition-colors hover:bg-green-50"
                      >
                        <FileText className="mr-3 w-4 h-4" />
                        Upload Docs
                      </button>
                    )}

                    {/* Divider */}
                    <div className="my-1 border-t border-gray-100"></div>

                    {/* Delete */}
                    <button
                      onClick={() => {
                        if (onDelete) {
                          onDelete();
                        }
                        setShowMoreActions(false);
                      }}
                      className="flex items-center px-4 py-2 w-full text-sm text-red-600 transition-colors hover:bg-red-50"
                    >
                      <Trash2 className="mr-3 w-4 h-4" />
                      Delete Trip
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>


      {/* Commission Edit Modal */}
      <CommissionEditModal
        isOpen={showCommissionModal}
        onClose={() => setShowCommissionModal(false)}
        trip={trip}
        onCommissionUpdate={handleCommissionUpdate}
      />

      {/* Questionnaire Summary Modal (trip recap) */}
      <QuestionnaireSummaryModal
        isOpen={showAgentNotesModal}
        onClose={() => setShowAgentNotesModal(false)}
        trip={trip}
        itinerary={trip} // Pass trip as itinerary for now
        client={client}
      />

      {/* Trip Notes Modal - Trip-specific notes (separate from Client Notes) */}
      <TripNotesEditorModal
        tripId={trip?.tripid}
        tripName={decodeHtmlEntities(trip?.tripname || trip?.name || "")}
        isOpen={showClientNotesModal}
        onClose={() => setShowClientNotesModal(false)}
      />

      {/* Share Client Modal */}
      <ShareClientModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        tripId={trip.tripid}
        clientName={client?.fullname || client?.name || client?.clientName || "your client"}
      />
    </div>
  );
};

export default EnhancedTripRow;